<script setup lang="ts">
import type { WindowElementMap } from './AppNavigationWindow2.vue'
import { NuxtLink, AppSearchInput } from '#components'

const { myLink } = useLinks()
const t2 = useT2({
  Shop: 'Pirkti',
  'Sign in': 'Prisijungti',
  // 'Shop products': 'Prekių katalogas',
  Wholesale: 'Didmena',
  'Retail Store': 'Mažmena',
})
// const locale = useLocale()
const auth = useAuth()
const route = useRoute()
const isHome = computed(() => myLinkName(route) === 'index')
const isB2b = useIsB2B()
const staging = useFast()
const b2bLink = computed(() => (auth.value.isB2B ? myLink('b2b-search') : myLink('b2b')))

defineProps<{
  productCount: number
  // seller: ReturnType<typeof useSeller>
}>()

// const sell = computed(() => {
//   const seller = props.seller.seller.value
//   const isDefault = props.seller.isDefault.value
//   const doc = seller?.doc
//   return {
//     doc,
//     handle: isDefault ? undefined : doc?.handle,
//     product: productCountLabel(seller?.productCount),
//   }
// })

// ------------   windows   ------------

const refs = useRefs({
  logo: undefined,
  navigationLeft: undefined,
  searchInput: AppSearchInput,
  sellerLogo: NuxtLink,
  sellerAbout: NuxtLink,
  sellerCatalog: NuxtLink,
  sellerEditorial: NuxtLink,
  windowCategories: NuxtLink,
  windowCategories2: NuxtLink,
  windowCategories3: NuxtLink,
  menuInfo: undefined,
  menuLang: undefined,
  menuCart: undefined,
  menuNofification: undefined,
  menuAuth: undefined,
})

export type WindowType =
  '' | `catalog:${1 | 2 | 3}` | 'search' | `menu:${'info' | 'cart' | 'lang' | 'auth'}` | `seller:${'catalog' | `about:${1 | 2}` | 'editorial'}` | 'catalog-primary' | 'menu:notifications'

const timer = useTimeout()
const { scrollTop, scrollingDown } = useScroll()
const init3 = useInited('init3')

const wind = shallowReactive({
  init2: true,
  init: false,
  type: '' as WindowType,
  elem: undefined as undefined | WindowElementMap,
  navState: { isVisible: true, isFixed: false } as { isFixed: boolean; isVisible: boolean; noAnimation?: boolean },
})

const startDone = useDelayInit(100)
const start = computed((): boolean => !startDone.value || auth.value.loading)
const timer2 = useTimeout()
const off = watch(
  () => !start.value,
  (on) =>
    on &&
    timer2.set(() => {
      wind.init2 = false
      off()
    }, 100)
)

function windowTypeSet(value: WindowType | 'close') {
  timer.clear()
  const el = helpers.getHTMLElement(refs.logo.value)
  if (el && getFocusedElement() === el) el.blur?.()
  if (value !== 'search') refs.searchInput.value?.blur()

  if (value === 'close' || !value) {
    // Off
    if (!value && wind.elem) return
    wind.elem = undefined
    wind.type = ''
    return
  }

  if (wind.type === value) return
  if (!wind.init) wind.init = true
  timer.set(
    () => {
      wind.type = value
      wind.elem = currentElemMap[value]
    },
    wind.elem ? 40 : 200
  )
}

const currentElemMap: Record<WindowType, WindowElementMap | undefined> = {
  '': undefined,
  'menu:auth': { elem: () => refs.menuAuth.value, type: 'menu' },
  'menu:cart': { elem: () => refs.menuCart.value, type: 'menu' },
  'menu:notifications': { elem: () => refs.menuNofification.value, type: 'menu' },
  'menu:info': { elem: () => refs.menuInfo.value, type: 'menu' },
  'menu:lang': { elem: () => refs.menuLang.value, type: 'menu' },
  search: { elem: () => refs.searchInput.value, type: 'search' },
  'catalog-primary': { elem: () => refs.navigationLeft.value, type: 'catalog-primary' },
  'catalog:1': { elem: () => refs.navigationLeft.value, type: 'categories' },
  'catalog:2': { elem: () => refs.windowCategories2.value, type: 'categories' },
  'catalog:3': { elem: () => refs.windowCategories3.value, type: 'categories' },
  'seller:about:1': { elem: () => refs.sellerLogo.value, type: 'categories' },
  'seller:about:2': { elem: () => refs.sellerLogo.value, type: 'categories' },
  'seller:editorial': { elem: () => refs.sellerEditorial.value, type: 'categories' },
  'seller:catalog': { elem: () => refs.sellerCatalog.value, type: 'categories' },
}

// nav state

function navStateSet(v: typeof wind.navState) {
  const p = wind.navState
  if (p.isFixed !== v.isFixed || p.isVisible !== v.isVisible || p.noAnimation !== v.noAnimation) {
    wind.navState = v
  }
}

watch(
  () => ({ isDown: scrollingDown.value, top: scrollTop.value, isOpened: !!wind.type?.length }),
  ({ isDown, top, isOpened }) => {
    if (isOpened) return navStateSet({ isFixed: true, isVisible: true, noAnimation: true })
    const el = helpers.getHTMLElement(refs.navigationLeft.value)
    if (!el) return navStateSet({ isVisible: true, isFixed: false })
    const navTop = myElem(el, 'offsetTop', 0)
    if (!isDown) return navStateSet({ isFixed: navTop < top, isVisible: true })
    const navBottom = navTop + myElem(el, 'offsetHeight', 0)
    const isFixed = navBottom < top + 5
    return navStateSet({ isFixed, isVisible: !isFixed, noAnimation: top < navBottom + 10 })
  }
)

const navClass = computed(() => {
  return {
    active: !!wind.type,
    home: isHome.value,
    start: start.value,
    init2: wind.init2,
    navFixed: wind.navState.isFixed,
    navVisible: wind.navState.isVisible,
    navNoAnimation: !!wind.navState.noAnimation,
  } satisfies Record<string, boolean>
})

const catActive = computed(() => helpers.isIn(wind.type, 'catalog-primary', 'catalog:1'))
const activeCatPage = computed(() => (catActive.value ? cat.value.page : undefined))

const {
  navigation,
  pageSet,
  data: cat,
  timerClear,
} = useNavigation({
  open: (on) => on && !catActive.value && windowTypeSet('catalog-primary'),
})

watch(
  () => ({ name: route.name, params: route.params }),
  () => windowTypeSet('close')
)
</script>

<template>
  <nav :class="[$style.nav, navClass]">
    <div :class="$style.navBg" />

    <div :ref="refs.navigationLeft" :class="$style.navLeft">
      <NuxtLink :ref="refs.logo" :to="myLink('index', {})" :class="$style.logo1" :aria-label="`${SEO_NAME} logo`" @mouseenter="windowTypeSet('')">
        <IconLogoFull3 :class="$style.logo" />
      </NuxtLink>

      <div class="_flex _row">
        <!-- <template v-if="sell.handle">
          <NuxtLink
            :ref="refs.sellerLogo"
            :to="myLink('@seller', { seller: sell.handle })"
            :class="$style.sellerLink"
            @mouseenter="windowTypeSet('seller:about:1')"
            @mouseleave="windowTypeSet('')"
          >
            <BaseImage
              v-if="sell.doc?.logo"
              :class="$style.sellerLogo"
              :img="sell.doc?.logo"
              sizes="100px"
              ratio="square"
            />
            <span :class="$style.name">{{ sell.doc?.name }}</span>
          </NuxtLink>
          <NuxtLink
            :ref="refs.sellerAbout"
            :to="myLink('@seller-info', { seller: sell.handle })"
            :class="[$style.shopButton, { on: helpers.isIn(wind.type, 'seller:about:1', 'seller:about:2') }]"
            :active-class="$style.linkOn"
            @mouseenter="windowTypeSet('seller:about:2')"
            @mouseleave="windowTypeSet('')"
          >{{ t2`About` }}</NuxtLink>
          <!- @mouseenter="windowTypeSet('seller:about')" ->

          <NuxtLink
            :ref="refs.sellerEditorial"
            :to="myLink('@seller-editorial', { seller: sell.handle })"
            :class="[$style.shopButton, { on: wind.type === 'seller:editorial' }]"
            :active-class="$style.linkOn"
            @mouseenter="windowTypeSet('')"
            @mouseleave="windowTypeSet('')"
          >{{ t2`Editorials` }}</NuxtLink>
          <NuxtLink
            :ref="refs.sellerCatalog"
            :to="myLink('@seller-s', { seller: sell.handle })"
            :class="[$style.shopButton, { on: wind.type === 'seller:catalog' }]"
            :active-class="$style.linkOn"
            @mouseenter="windowTypeSet('seller:catalog')"
            @mouseleave="windowTypeSet('')"
          >{{ sell.product }}</NuxtLink>
        </template> -->
        <!--  -->
        <!-- <NuxtLink :class="$style.shopButton" :to="myLink('brands', {})" @mouseenter="windowTypeSet('')">{{
            t2`Brands`
          }}</NuxtLink> -->
        <!-- <NuxtLink :class="$style.shopButton" :to="{ name: 'editorial' }" @mouseenter="windowTypeSet('')">{{
            t2`Editorials`
          }}</NuxtLink> -->
        <!-- <NuxtLink
            :ref="refs.windowCategories3"
            :class="$style.shopButton"
            :to="{ name: 'search', query: { limit: CatalogSearchLimitTo.Upcoming } }"
            @mouseenter="windowTypeSet('catalog:3')"
            @mouseleave="windowTypeSet('')"
          >{{ t2`Upcoming` }}</NuxtLink> -->
        <!-- <NuxtLink
            :ref="refs.windowCategories2"
            :class="[$style.shopButton]"
            :to="{ name: 'search', query: { limit: CatalogSearchLimitTo.Sale } }"
            @mouseenter="windowTypeSet('catalog:2')"
            @mouseleave="windowTypeSet('')"
          >{{ t2`Today's deals` }}</NuxtLink> -->

        <NuxtLink
          :ref="refs.windowCategories"
          :class="[$style.shopButton, { on: helpers.isIn(wind.type, 'catalog:1', 'catalog:2', 'catalog:3') }]"
          :to="myLink('search')"
          @mouseenter.passive="windowTypeSet('catalog:1')"
          @mouseleave.passive="windowTypeSet('')"
        >
          <span>{{ t2('Retail Store') }}</span>
          <!-- <template v-else-if="locale === 'lt'"
            >Katalogas -&nbsp;<em class="_ls-xs _fw-xl">{{ productCount }}</em
            >&nbsp;prekių</template
          >
          <template v-else
            >Shop&nbsp;<em class="_ls-xs _fw-xl">{{ productCount }}</em
            >&nbsp;products</template
          > -->
        </NuxtLink>

        <template v-if="staging">
          <NuxtLink :class="[$style.shopButton, isB2b && 'on']" class="_gap04" :to="b2bLink" @mouseenter.passive="windowTypeSet('')">
            <span class="_fs09 _fw-xl">B2B</span>
            <span class="_c4">/</span>
            <span class="fs07">{{ t2('Wholesale') }}</span>
          </NuxtLink>
        </template>
      </div>
    </div>

    <div :class="[$style.navRight, $clsClass('nav-right')]">
      <AppSearchInput :ref="refs.searchInput" :on-input="() => windowTypeSet('search')" :on-focus="() => windowTypeSet('search')" />

      <div :class="$style.menu">
        <div
          :ref="refs.menuInfo"
          tabindex="0"
          role="button"
          :class="[$style.btn, wind.type === 'menu:info' ? 'on' : 'off']"
          @mouseenter.passive="windowTypeSet('menu:info')"
          @mouseleave.passive="windowTypeSet('')"
        >
          <NuxtLink :to="myLink('info')" class="_gap04 _row _inline _ctrY" aria-label="Customer Care Information">
            <span>{{ t2('Customer Care') }}</span>
            <IconInfo :class="$style.info" />
          </NuxtLink>
        </div>

        <div
          :ref="refs.menuLang"
          tabindex="0"
          role="button"
          :class="[$style.btn, wind.type === 'menu:lang' ? 'on' : 'off']"
          @mouseenter.passive="windowTypeSet('menu:lang')"
          @mouseleave.passive="windowTypeSet('')"
        >
          <AppLanguageUi />
        </div>

        <div
          :ref="refs.menuAuth"
          tabindex="0"
          role="button"
          :class="[$style.btn, wind.type === 'menu:auth' ? 'on' : 'off']"
          @mouseenter.passive="windowTypeSet('menu:auth')"
          @mouseleave.passive="windowTypeSet('')"
        >
          <NuxtLink v-if="auth.isAuth" :to="myLink('user-orders')"><IconProfile /></NuxtLink>
          <NuxtLink v-else :class="$style.login" :to="myLink('auth')"
            ><span class="_mt-1">{{ t2('Sign in') }}</span
            ><IconProfile class="_svg18" stroke="1.5"
          /></NuxtLink>
        </div>

        <div
          v-if="auth.isAuth"
          :ref="refs.menuNofification"
          tabindex="0"
          role="button"
          aria-label="Notifications"
          :class="[$style.btn, wind.type === 'menu:notifications' ? 'on' : 'off']"
          @mouseenter.passive="windowTypeSet('menu:notifications')"
          @mouseleave.passive="windowTypeSet('')"
        >
          <AppNotifications />
        </div>
        <div
          :ref="refs.menuCart"
          tabindex="0"
          role="button"
          aria-label="Open cart view"
          :class="[$style.btn, wind.type === 'menu:cart' ? 'on' : 'off']"
          @mouseenter.passive="windowTypeSet('menu:cart')"
          @mouseleave.passive="windowTypeSet('')"
        >
          <NuxtLink :to="myLink('cart')" aria-label="Go to cart view">
            <CartIcon :count="auth.cartSize" :added-once="() => windowTypeSet('menu:cart')" />
          </NuxtLink>
        </div>
      </div>
    </div>

    <div v-if="!isB2b" :class="[$style.cat, catActive && 'on']" itemscope="true" itemtype="http://schema.org/SiteNavigationElement" @mouseleave.passive="() => timerClear()">
      <NuxtLink
        v-for="(item, index) in navigation"
        :key="index"
        :class="[$style.linkA, activeCatPage === item.page && 'on']"
        itemprop="name"
        :to="item.to"
        @mouseenter.passive="() => pageSet(item.page)"
        ><span :class="[$style.link, item.red ? '_red' : '_c1']"
          ><span :class="$style.link2">{{ item.page.title?.text?.value }}</span
          >{{ item.page.title?.text?.value }}</span
        ></NuxtLink
      >
    </div>

    <LazyAppNavigationWindow2 v-if="wind.init || init3" :elem="wind.elem" :window-type="wind.type" :exit="() => windowTypeSet('close')" />
  </nav>
</template>

<style module>
.nav {
  composes: global(_absolute);
  composes: global(_w100p);
  top: 0;
  left: 0;
  right: 0;
}
.info {
  composes: global(_svg16);
  composes: global(_c2);
  margin-left: 2px;
}
.bg {
  top: 0;
  height: var(--nav-h);
}

.nav:global(.start) .bg {
  opacity: 0.01;
}
.nav:global(.init2) .bg {
  transition: opacity 500ms var(--tran2);
}

.nav:global(.navFixed) .bg {
  position: fixed;
  transform: translateY(-100%);
  transition: transform 300ms ease;
}
.nav:hover .bg,
.nav:global(.active) .bg,
.nav:global(.navFixed) .bg {
  opacity: 0.99;
}

.nav:global(.navVisible) .bg {
  transform: translateY(0);
}

.nav:global(.navNoAnimation) .bg {
  transition: none;
}

.navBg {
  composes: bg;
  composes: global(_fixed-start);
  composes: global(_w100p);
  composes: global(_bg0);

  z-index: 95;
  opacity: 0.001;
  transition: opacity 300ms ease;
}

.navRight,
.navLeft {
  composes: global(_absolute);
  composes: global(_row);
  composes: bg;
  z-index: 100;
}

.navRight {
  right: 0;
  composes: global(_pt04);
  composes: global(_pr04);
}
.navLeft {
  --color: var(--black);
  left: 0;
}

.shopButton {
  composes: global(_ctr);
  composes: global(_fs09);
  composes: global(_fw-l);
  composes: global(_pr1);
  composes: global(_pl1);
  composes: global(_relative);
  composes: global(_h100p);
  background: transparent;
}

.shopButton:hover {
  background: var(--black-5);
}

.shopButton:global(.on) {
  background: var(--blue3);
  color: var(--bg);
}

/* buttons */

.menu {
  composes: global(_row);
  composes: global(_fs09);
  composes: global(_relative);
  composes: global(_pt02);
  composes: global(_end);

  height: inherit;
  --nav-color: var(--black);
  --nav-color-off: var(--black-3);
  color: var(--nav-color);
  z-index: 1;
}
.btn {
  composes: global(_row);
  composes: global(_ctrY);
  composes: global(_pl04);
  composes: global(_pr04);
  composes: global(_relative);
  composes: global(_h34);
  composes: global(_ml06);

  background: transparent;
  outline: none;
  border: none;

  color: var(--nav-color);
}
.btn:last-of-type {
  margin-right: 4px;
  padding-right: 10px;
}
.btn::after {
  position: absolute;
  content: '';

  height: 1px;
  width: 80%;
  max-width: 100px;

  background: transparent;
  inset: auto 0 2px;
  margin: auto;
  transition: background 300ms linear;
}
.btn:global(.on)::after {
  background: var(--nav-color);
}
.login {
  composes: global(_pl08);
  composes: global(_pr04);
  composes: global(_h18);
  composes: global(_fw-xl);
  composes: global(_gap08);
  composes: global(_ctr);
  composes: global(_br99);
  composes: global(_c0);
  background: var(--blue3);
}

/* seller */

.logo1 {
  /* fill="#042E47" */
  --color: #002855;
  width: var(--nav-h);
  height: var(--nav-h);

  composes: global(_ctr);

  width: calc(1657 / (224 * var(--h)));
  --h: 26px;
  color: var(--color);
  composes: global(_pl1);
  composes: global(_pr1);
}

.logo {
  aspect-ratio: 1657 / 224;
  height: var(--h);
  width: auto;
  aspect-ratio: 250 / 31;
}

/* .mix {
  mix-blend-mode: difference;
  color: #fff;
}

.nav:global(.active) .mix,
.nav:hover .mix,
.nav:global(.navFixed) .mix,
.nav:global(.navFixed):global(.navVisible) .mix {
  mix-blend-mode: normal;
  color: var(--color);
} */
/*
.navLeft, .logo1 {
  composes: mix;
} */

.navLeft .logo1:hover,
.navLeft .logo1:focus,
.navLeft .logo1:active {
  background: var(--blue3);
  --color: var(--bg);
  /* mix-blend-mode: unset; */
}

.navBg {
  height: calc(var(--nav-h) * 1);
}
.nav:global(.navFixed) .navBg {
  height: calc(var(--nav-h) * 1);
}

/* .nav:hover .link,
.nav:global(.active) .link,
.nav:global(.navFixed) .link {
  mix-blend-mode: normal;
  color: var(--black);
  background: var(--bg);
} */

/* .logoBig {
  aspect-ratio: 1657 / 224;
  height: var(--h);
  filter: var(--filter);
  width: auto;
} */

.cat {
  composes: global(_row);
  composes: global(_ctrY);
  composes: global(_wcont);
  composes: global(_ml04);

  padding-top: var(--nav-h);
}

.nav:global(.start) .cat {
  opacity: 0.01;
}
.nav:global(.init2) .cat {
  transition: opacity 500ms var(--tran2);
}

.nav .cat:global(.on),
.nav:hover .cat,
.cat:hover,
.nav:global(.home) .cat,
.nav:global(.active) .cat {
  visibility: visible;
}

.linkA {
  /* mix-blend-mode: difference; */
  z-index: 94;
  composes: global(_pt04);
  composes: global(_pr02);
}
.nav:hover .linkA,
.cat:global(.on) .linkA,
.linkA:hover {
  /* mix-blend-mode: normal; */
  z-index: 97;
}

.link,
.link2 {
  composes: global(_ctr);
  composes: global(_br99);
  composes: global(_hidden);
}

.link {
  composes: global(_relative);
  composes: global(_ff3);
  composes: global(_fs09);
  composes: global(_fw-l);
  composes: global(_ls-xs);
  composes: global(_pl1);
  composes: global(_pr1);

  composes: global(_txt_ctr);

  min-width: var(--nav-h);
  height: var(--nav-h);
}

.link2 {
  composes: global(_absolute);
  composes: global(_inset);
  background: var(--c, var(--blue3));
  composes: global(_c0);
  z-index: 1;
  clip-path: inset(7px 7px calc(100% - 7px) 7px round 99px);
  transition: clip-path 0.45s cubic-bezier(0.23, 1, 0.32, 1);
}

.cat:global(.on) .link,
.nav:hover .link {
  color: var(--c, var(--black));
  background: var(--bg);
}

.link:global(._red) {
  --c: var(--red);
  color: var(--c);
}

.linkA:hover .link2,
.linkA:global(.on) .link2 {
  clip-path: inset(0 0 0 0 round 99px);
}
</style>
